import dayjs from 'dayjs'
import type { Channel } from '../brokerApi/channels/channel'
import type { EventSource } from '../eventsApi/event-sources/event-source.entity'
import type { Invoice } from '../posApi/invoices/invoice'
import type { Service } from '../posApi/services/service'
import type { Appointment } from '../schedulerApi/appointments/appointment'
import type { Person } from '../eventsApi/persons/person'

// == regras de negócio (constantes) ============================================================
// ligação (voip/celular/telefone) só conta como "contato respondido" a partir de X segundos de duração
const MIN_CALL_SECONDS_TO_CONSIDER_CONNECTED = 60
// troca de mensagens (sms/email/whatsapp) só conta como "contato respondido" a partir de X mensagens
const MIN_MESSAGES_TO_CONSIDER_CONNECTED = 5
// até quantos meses atrás uma compra ainda classifica o cliente como "comprador antigo" (buyer) — usado por
// loadBuyers() e por quem alimenta loadBuyersSource() (dashboard-xray.svelte, goalboard-members.svelte)
export const KPI_OLD_BUYER_LOOKBACK_MONTHS = 18
// invoices são buscadas com esse tanto de meses de folga além do fim do período, pois a janela de conversão
// (conversionWindowDays, parâmetro de getConversions) pode chegar a até 90 dias após o agendamento
const INVOICE_LOOKAHEAD_MONTHS_FOR_CONVERSION_WINDOW = 3

export interface KPIIndicators_iScheduleResult {
    scheduled: number
    showFromMine: number
    showToMe: number
    showFromMinePercentual: number
    details: KPIIndicators_iSchedule
}

export interface KPIIndicators_iCommunicationResult {
    contacted: number
    connected: number
    details: KPIIndicators_iCommunication
}

export interface KPIIndicators_iConversionResult {
    convertedByMe: number
    convertedFromMine: number
    convertedByMePercentual: number
    convertedFromMinePercentual: number
    ticket: number
    ticketByMe: number
    ticketFromMine: number
    totalValueByMe: number
    totalValueFromMine: number
    totalCount: number
    totalValue: number
    totalCountRebuy: number
    totalValueRebuy: number
    totalDirectCount: number
    totalDirectValue: number
    totalOldCount: number
    totalOldValue: number
    details: KPIIndicators_iConversion
}

// Ciclo de vida em 2 fases, obrigatório e verificado em runtime (ver assertLoaded/buyersSourceLoaded):
//   1) loadBuyersSource(from, to) — uma vez, cobrindo a janela de TODOS os meses que serão processados.
//   2) refreshData(from, to) — uma vez por período (ex: uma vez por mês); populam os campos privados abaixo.
//   3) getCommunication / getSchedulesByCreationDate / getSchedulesByAppointmentDate / getConversions —
//      leem o estado deixado pela refreshData() mais recente. Chamar antes de um refreshData() correspondente
//      agora lança erro em vez de retornar dados zerados silenciosamente.
// Reaproveite a MESMA instância entre os passos 2/3 de um mesmo período; para outro período, chame
// refreshData() de novo (não é preciso repetir loadBuyersSource()).
export class KPIIndicators {
    private consulting: string[] = []
    private eventSources: EventSource[] = []
    private consultingsCreated: Appointment[] = []
    private consultingsAt: Appointment[] = []
    private buyers: { personId: string }[] = []
    // base bruta (não deduplicada) de compradores, cobrindo a janela de TODOS os meses do relatório de uma
    // vez — ver loadBuyersSource(). loadBuyers() só recorta/filtra em memória a partir daqui, sem chamar a API.
    private buyersSource: { personId: string; createdAt: Date }[] = []
    private invoices: Invoice[] = []
    private channels: Channel[] = []
    private dateFrom: Date
    private dateTo: Date

    private buyersSourceLoaded = false
    // true somente entre o fim de um refreshData() e o início do próximo — os getters exigem isso.
    private loaded = false

    private assertLoaded() {
        if (!this.loaded) {
            throw new Error(
                'KPIIndicators: chame refreshData() antes de ler os indicadores (getCommunication/getSchedulesBy*/getConversions).',
            )
        }
    }

    // busca UMA vez a base de compradores cobrindo a janela de 18 meses de TODOS os meses que serão
    // processados por refreshData() nesta sessão de carregamento — as janelas individuais de cada mês se
    // sobrepõem quase inteiramente (deslocam só 1 mês entre si), então repetir a consulta uma vez por mês
    // é trabalho redundante. Chamar antes do primeiro refreshData(). Sem cache — busca sempre fresco.
    async loadBuyersSource(from: Date, to: Date) {
        this.buyersSource = await window.api.pos.invoices.getBuyers(from, to)
        this.buyersSourceLoaded = true
    }

    async refreshData(from: Date, to: Date) {
        if (!this.buyersSourceLoaded) {
            throw new Error('KPIIndicators: chame loadBuyersSource() antes de refreshData().')
        }
        this.loaded = false
        this.dateFrom = new Date(from)
        this.dateTo = new Date(to)
        await this.loadChannels()
        this.loadBuyers(from)
        await this.loadConsultingServices() // first always
        await this.loadConsultingsAdded(from, to)
        await this.loadConsultingsAt(from, to)
        await this.loadEventSources(from, to)
        await this.loadInvoices(from, to)
        this.loaded = true
        return {
            dataCount:
                this.consulting.length +
                this.eventSources.length +
                this.consultingsCreated.length +
                this.consultingsAt.length +
                this.invoices.length +
                this.channels.length +
                this.buyers.length,
        }
    }

    private async loadChannels() {
        this.channels = await window.api.brokers.channels.getAll({ updateStatus: false }, 600)
    }

    // filtra buyersSource (já em memória) pra janela de 18 meses ESTE mês específico — "to" é sempre "from"
    // pois não queremos que compras do próprio mês sejam consideradas buyers (cliente antigo).
    private loadBuyers(from: Date) {
        let oldFrom = new Date(from)
        oldFrom.setMonth(oldFrom.getMonth() - KPI_OLD_BUYER_LOOKBACK_MONTHS)
        const personIds = new Set<string>()
        for (const buyer of this.buyersSource) {
            const createdAt = new Date(buyer.createdAt)
            if (createdAt >= oldFrom && createdAt < from) personIds.add(buyer.personId)
        }
        this.buyers = [...personIds].map((personId) => ({ personId }))
    }

    private async loadConsultingServices() {
        const services: Service[] = await window.api.pos.services.getAll({ consulting: true }, 600)
        this.consulting = services.map((el) => el.serviceId)
        return
    }

    private async loadEventSources(from: Date, to: Date) {
        let params = {
            from: new Date(from),
            to: new Date(to),
            sources: ['whatsapp', 'voip', 'telefone', 'sms', 'email'],
        }
        this.eventSources = await window.api.events.eventSources.getAllFiltered(params, 600)
        for (const es of this.eventSources) {
            es.channel = this.channels.find((el) => el.channelId === es.channelId)
        }
    }

    private async loadConsultingsAdded(from: Date, to: Date) {
        this.consultingsCreated = await window.api.schedulers.appointments.getConsulting(from, to, this.consulting, true)
        return
    }

    private async loadConsultingsAt(from: Date, to: Date) {
        this.consultingsAt = await window.api.schedulers.appointments.getConsulting(from, to, this.consulting, false)
        return
    }

    private async loadOldConsultings(dateLimitTo: Date, persons: string[], pastLimit: number) {
        const fromWindow = new Date(dateLimitTo)
        fromWindow.setDate(fromWindow.getDate() - pastLimit)
        const result = await window.api.schedulers.appointments.getAll({
            from: fromWindow,
            to: dateLimitTo,
            serviceId: this.consulting,
            personId: persons,
            fromCreatedAt: true, // necessário pois o 'loadConsultings' também usa o 'fromCreatedAt'. se usarmos o 'start'ficari aum gap entre a criação e a data de agendamento. o 'loadConsultings' usa o 'createdAt' pois é um indicador direto de conversão dos agendamentos INCLUÍDOS no período atual, e não realizados.
        })
        return result
    }

    private async loadInvoices(from: Date, to: Date) {
        const _to = new Date(to)
        // busca com folga: a janela de conversão (getConversions) pode olhar compras de até 90 dias após o agendamento
        _to.setMonth(_to.getMonth() + INVOICE_LOOKAHEAD_MONTHS_FOR_CONVERSION_WINDOW)
        this.invoices = await window.api.pos.invoices.getAll(
            { from, to: _to, service: true, fields: ['personId', 'total', 'services', 'createdAt'] },
            600,
        )
    }

    // =================================================================================================
    // =================================================================================================
    // =================================================================================================

    async getCommunication(userId: string = null): Promise<KPIIndicators_iCommunicationResult> {
        this.assertLoaded()
        const details = { type: 'communications', persons: [], connected: [] } as KPIIndicators_iCommunication
        const buyerPersonIds = new Set(this.buyers.map((el) => el.personId))

        let contacted = 0
        let connected = 0
        const personsContacted = new Set<string>()
        const personsConnected = new Map<string, { count: number; added: boolean }>()

        for (const es of this.eventSources) {
            es.externalDate = new Date(es.externalDate)
            // se é cliente antigo ignora
            if (buyerPersonIds.has(es.personId)) continue
            if (userId && userId !== es.channel?.userId) continue
            if (es.lastEvent !== 'comunicacao_enviada') {
                // se é voip, celular ou telefone, permite comunicacao_recebida. whats não permite pois será contabilizado apenas se o atendente responder. já o telefone, na ligação de entrada, pode haver uma conversa.
                if (es.source !== 'voip' && es.source !== 'celular' && es.source !== 'telefone') continue
                if (es.lastEvent !== 'comunicacao_recebida') continue
            }

            // add details
            let detailPerson = details.persons.find((el) => el.personId === es.personId)
            if (!detailPerson) {
                detailPerson = {
                    personId: es.personId,
                    all: [],
                }
                details.persons.push(detailPerson)
            }
            detailPerson.all.push(es)

            // adiciona CONTACTADOS
            if (!personsContacted.has(es.personId)) {
                personsContacted.add(es.personId)
                contacted += 1
            }
            // adiciona COMUNICAÇÃO COM SUCESSO
            let found = personsConnected.get(es.personId)
            if (!found) {
                found = { count: 0, added: false }
                personsConnected.set(es.personId, found)
            }
            if (!found.added) {
                // telefone / voz
                if (es.source === 'voip' || es.source === 'celular' || es.source === 'telefone') {
                    if (es.value >= MIN_CALL_SECONDS_TO_CONSIDER_CONNECTED) {
                        connected += 1
                        found.added = true
                        details.connected.push(es.personId)
                    }
                    // outros (sms, email, whatsapp)
                } else {
                    found.count += 1
                    if (found.count >= MIN_MESSAGES_TO_CONSIDER_CONNECTED) {
                        connected += 1
                        found.added = true
                        details.connected.push(es.personId)
                    }
                }
            }
        }
        return { contacted, connected, details }
    }

    // usada tanto por getSchedulesByCreationDate quanto por getSchedulesByAppointmentDate — a única diferença
    // entre as duas é qual lista de agendamentos é percorrida (criados no período vs marcados pro período).
    private computeSchedules(appointments: Appointment[], userId: string | null): KPIIndicators_iScheduleResult {
        const details = { type: 'appointments', persons: [], show: [] } as KPIIndicators_iSchedule

        const personsScheduled = new Set<string>()
        const personsShowFromMine = new Set<string>()
        const personsShowToMe = new Set<string>()
        let scheduled = 0
        let showFromMine = 0
        let showToMe = 0

        for (const appointment of appointments) {
            // add details
            let detailPerson = details.persons.find((el) => el.personId === appointment.personId)
            if (!detailPerson) {
                detailPerson = {
                    personId: appointment.personId,
                    all: [],
                }
                details.persons.push(detailPerson)
            }
            detailPerson.all.push(appointment)

            if (!userId || appointment.appointment_userId === userId) {
                if (!personsScheduled.has(appointment.personId)) {
                    personsScheduled.add(appointment.personId)
                    scheduled += 1
                }
            }
            // meus agendamentos que compareceram
            if (!userId || appointment.appointment_userId === userId) {
                if (appointment.status === 'agendamento_compareceu') {
                    if (!personsShowFromMine.has(appointment.personId)) {
                        personsShowFromMine.add(appointment.personId)
                        showFromMine += 1
                        details.show.push(appointment.personId)
                    }
                }
            }
            // agendamentos que compareceram PARA MIM
            if (!userId || appointment.userId === userId) {
                if (appointment.status === 'agendamento_compareceu') {
                    if (!personsShowToMe.has(appointment.personId)) {
                        personsShowToMe.add(appointment.personId)
                        showToMe += 1
                        details.show.push(appointment.personId)
                    }
                }
            }
        }
        const showFromMinePercentual = scheduled === 0 ? 0 : parseInt(((100 / scheduled) * showFromMine).toFixed(0))

        return {
            scheduled,
            showFromMine,
            showToMe,
            showFromMinePercentual,
            details,
        }
    }

    // agendamentos CRIADOS dentro do período (indicador de conversão dos contatos feitos no período).
    async getSchedulesByCreationDate(userId: string = null): Promise<KPIIndicators_iScheduleResult> {
        this.assertLoaded()
        return this.computeSchedules(this.consultingsCreated, userId)
    }

    // agendamentos MARCADOS para dentro do período (indicador de presença/no-show do período).
    async getSchedulesByAppointmentDate(userId: string = null): Promise<KPIIndicators_iScheduleResult> {
        this.assertLoaded()
        return this.computeSchedules(this.consultingsAt, userId)
    }

    async getConversions(
        conversionWindowDays: number,
        userId: string = null,
        scheduleShowToMe: number,
        scheduleShowFromMine: number,
        conversionsBased: 'consultingsCreated' | 'consultingsAt',
    ): Promise<KPIIndicators_iConversionResult> {
        this.assertLoaded()
        const details = {
            type: 'conversions',
            persons: [],
            rebuy: [],
            converted: [],
            direct: [],
            old: [],
            all: [],
        } as KPIIndicators_iConversion

        const conversionAppointments = this[conversionsBased]
        const conversionApPersonIds = new Set(conversionAppointments.map((ap) => ap.personId))
        const buyerPersonIds = new Set(this.buyers.map((el) => el.personId))

        // pessoas não-recorrentes (não estão em buyers) que têm um agendamento ANTIGO — usado pra não contar
        // como "compra direta" quem na verdade converteu de um agendamento de fora da janela de conversão.
        const notRecurringPersonIds = [...new Set(this.invoices.filter((el) => !buyerPersonIds.has(el.personId)).map((el) => el.personId))]
        const oldConsultings = await this.loadOldConsultings(this.dateFrom, notRecurringPersonIds, conversionWindowDays)
        const oldConsultingPersonIds = new Set<string>(oldConsultings.map((el: { personId: string }) => el.personId))

        const purchaseTotals = {
            totalCount: 0,
            totalValue: 0,
            totalCountRebuy: 0,
            totalValueRebuy: 0,
            totalDirectCount: 0,
            totalDirectValue: 0,
            totalOldCount: 0,
            totalOldValue: 0,
        }
        const seenPurchasePersons = {
            total: new Set<string>(),
            recurring: new Set<string>(),
            direct: new Set<string>(),
            old: new Set<string>(),
        }

        const conversionTotals = { convertedByMe: 0, convertedFromMine: 0, totalValueByMe: 0, totalValueFromMine: 0 }
        const seenConversionPersons = { byMe: new Set<string>(), fromMine: new Set<string>() }
        const seenConversionItems = { byMe: new Set<string>(), fromMine: new Set<string>() }

        for (const invoice of this.invoices) {
            // add details
            let detailPerson = details.persons.find((el) => el.personId === invoice.personId)
            if (!detailPerson) {
                detailPerson = {
                    personId: invoice.personId,
                    all: [],
                }
                details.persons.push(detailPerson)
            }
            detailPerson.all.push(invoice)

            this.classifyPurchase(invoice, buyerPersonIds, oldConsultingPersonIds, conversionApPersonIds, seenPurchasePersons, purchaseTotals, details)
            this.classifyConversion(
                invoice,
                conversionAppointments,
                conversionWindowDays,
                userId,
                seenConversionPersons,
                seenConversionItems,
                conversionTotals,
                details,
            )
        }

        const ticket = purchaseTotals.totalCount <= 0 ? 0 : Math.round((purchaseTotals.totalValue / purchaseTotals.totalCount) * 100) / 100
        const ticketByMe =
            conversionTotals.convertedByMe <= 0 ? 0 : Math.round((conversionTotals.totalValueByMe / conversionTotals.convertedByMe) * 100) / 100
        const ticketFromMine =
            conversionTotals.convertedFromMine <= 0
                ? 0
                : Math.round((conversionTotals.totalValueFromMine / conversionTotals.convertedFromMine) * 100) / 100
        const convertedByMePercentual =
            scheduleShowToMe === 0 ? 0 : parseInt(((100 / scheduleShowToMe) * conversionTotals.convertedByMe).toFixed(0))
        const convertedFromMinePercentual =
            scheduleShowFromMine === 0 ? 0 : parseInt(((100 / scheduleShowFromMine) * conversionTotals.convertedFromMine).toFixed(0))

        return {
            convertedByMe: conversionTotals.convertedByMe,
            convertedFromMine: conversionTotals.convertedFromMine,
            convertedByMePercentual,
            convertedFromMinePercentual,
            ticketByMe,
            ticketFromMine,
            totalValueByMe: conversionTotals.totalValueByMe,
            totalValueFromMine: conversionTotals.totalValueFromMine,
            ticket,
            totalCount: purchaseTotals.totalCount,
            totalValue: purchaseTotals.totalValue,
            totalCountRebuy: purchaseTotals.totalCountRebuy,
            totalValueRebuy: purchaseTotals.totalValueRebuy,
            details,
            totalDirectCount: purchaseTotals.totalDirectCount,
            totalDirectValue: purchaseTotals.totalDirectValue,
            totalOldCount: purchaseTotals.totalOldCount,
            totalOldValue: purchaseTotals.totalOldValue,
        }
    }

    // classifica UMA invoice em: recompra (cliente já comprava antes do período) / compra por agendamento
    // antigo (fora da janela de conversão) / compra direta (sem agendamento relacionado). Ignora invoices
    // fora do período (this.invoices também carrega registros futuros, usados só por classifyConversion).
    private classifyPurchase(
        invoice: Invoice,
        buyerPersonIds: Set<string>,
        oldConsultingPersonIds: Set<string>,
        conversionApPersonIds: Set<string>,
        seenPersons: { total: Set<string>; recurring: Set<string>; direct: Set<string>; old: Set<string> },
        totals: {
            totalCount: number
            totalValue: number
            totalCountRebuy: number
            totalValueRebuy: number
            totalDirectCount: number
            totalDirectValue: number
            totalOldCount: number
            totalOldValue: number
        },
        details: KPIIndicators_iConversion,
    ) {
        if (new Date(invoice.createdAt) > this.dateTo) return

        if (!seenPersons.total.has(invoice.personId)) {
            seenPersons.total.add(invoice.personId)
            totals.totalCount += 1
            details.all.push(invoice.personId)
        }
        totals.totalValue += invoice.total

        if (buyerPersonIds.has(invoice.personId)) {
            // recompra: cliente já era comprador antes do período
            if (!seenPersons.recurring.has(invoice.personId)) {
                seenPersons.recurring.add(invoice.personId)
                totals.totalCountRebuy += 1
                details.rebuy.push(invoice.personId)
            }
            totals.totalValueRebuy += invoice.total
            return
        }

        if (oldConsultingPersonIds.has(invoice.personId)) {
            // compra por agendamento antigo (fora da janela de conversão considerada)
            if (!seenPersons.old.has(invoice.personId)) {
                seenPersons.old.add(invoice.personId)
                totals.totalOldCount += 1
                details.old.push(invoice.personId)
            }
            totals.totalOldValue += invoice.total
            return
        }

        // compra direta: sem recompra nem agendamento antigo. Só conta em totalDirectCount se também não tiver
        // agendamento no período ATUAL — esse caso já é contabilizado como conversão por classifyConversion.
        if (!seenPersons.direct.has(invoice.personId)) {
            seenPersons.direct.add(invoice.personId)
            if (!conversionApPersonIds.has(invoice.personId)) {
                totals.totalDirectCount += 1
                details.direct.push(invoice.personId)
            }
        }
        totals.totalDirectValue += invoice.total
    }

    // verifica se UMA invoice converteu algum agendamento do período atual (dentro da janela de conversão) —
    // separado em "por mim" (quem vendeu o item) e "dos meus agendamentos" (quem marcou o agendamento).
    private classifyConversion(
        invoice: Invoice,
        conversionAppointments: Appointment[],
        conversionWindowDays: number,
        userId: string | null,
        seenPersons: { byMe: Set<string>; fromMine: Set<string> },
        seenItems: { byMe: Set<string>; fromMine: Set<string> },
        totals: { convertedByMe: number; convertedFromMine: number; totalValueByMe: number; totalValueFromMine: number },
        details: KPIIndicators_iConversion,
    ) {
        for (const item of invoice.services) {
            for (const ap of conversionAppointments) {
                if (ap.personId !== invoice.personId) continue
                // se já passou a janela de conversão então ignora
                const daysDiff = dayjs(invoice.createdAt).diff(ap.start, 'day')
                if (daysDiff > conversionWindowDays) continue

                // conversão do vendedor (quem vendeu o item na invoice)
                if (!userId || userId === item.userId) {
                    // o total de convertidos é por cliente único
                    if (!seenPersons.byMe.has(invoice.personId)) {
                        seenPersons.byMe.add(invoice.personId)
                        totals.convertedByMe += 1
                        details.converted.push(invoice.personId)
                    }
                    if (!seenItems.byMe.has(item.servicesOnInvoicesId)) {
                        seenItems.byMe.add(item.servicesOnInvoicesId)
                        totals.totalValueByMe += item.value * item.quantity
                    }
                }
                // conversão de quem marcou o agendamento
                if (!userId || userId === ap.appointment_userId) {
                    // o total de convertidos é por cliente único
                    if (!seenPersons.fromMine.has(invoice.personId)) {
                        seenPersons.fromMine.add(invoice.personId)
                        totals.convertedFromMine += 1
                        details.converted.push(invoice.personId)
                    }
                    if (!seenItems.fromMine.has(item.servicesOnInvoicesId)) {
                        seenItems.fromMine.add(item.servicesOnInvoicesId)
                        totals.totalValueFromMine += item.value * item.quantity
                    }
                }
            }
        }
    }
}

export interface KPIIndicators_iCommunication {
    type: 'communications' | 'appointments' | 'conversions'
    connected: string[]
    persons: {
        personId: string
        all: EventSource[]
        person?: Person
    }[]
}

export interface KPIIndicators_iSchedule {
    type: 'communications' | 'appointments' | 'conversions'
    show: string[]
    persons: {
        personId: string
        all: Appointment[]
        person?: Person
    }[]
}

export interface KPIIndicators_iConversion {
    type: 'communications' | 'appointments' | 'conversions'
    converted: string[]
    rebuy: string[]
    direct: string[]
    old: string[] // agendamentos antigos (utilizado para não exibir como conversões sem agendamento)
    all: string[] // all refere-se a todas as compra válidas (ou seja, dentro do mês)
    persons: {
        personId: string
        all: Invoice[] // all refere-se a todos os registros necessários para exibir os dados. mas nem todos são válidos para o período.
        person?: Person
    }[]
}
